import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { errorMessage, useToast } from '@/components/Toast';
import { Button, Field, Input } from '@/components/ui';
import { AuthAside } from './AuthAside';

export function RegisterPage() {
  const { status, register } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState({ email: '', username: '', password: '', displayName: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register({
        email: form.email.trim(),
        username: form.username.trim(),
        password: form.password,
        displayName: form.displayName.trim() || undefined,
      });
      // Registration signs the user straight in; the verification nudge rides
      // along as a toast so we keep the existing land-on-chat flow.
      toast.push('Account created — check your inbox to verify your email address.', 'ok');
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <AuthAside />
      <div className="auth-main">
        <div className="auth-card">
          <p className="eyebrow">Get started</p>
          <h1>Create your account</h1>

          <form className="stack gap-4" onSubmit={onSubmit}>
            <Field label="Display name" hint="Optional. How your name appears to colleagues in a shared workspace.">
              <Input value={form.displayName} onChange={set('displayName')} placeholder="Jane Doe" autoComplete="name" />
            </Field>
            <Field label="Email" hint="Used to verify the account and to reset your password." required>
              <Input type="email" value={form.email} onChange={set('email')} placeholder="you@firm.com" autoComplete="email" required />
            </Field>
            <Field label="Username" hint="3–100 characters. You can sign in with either this or your email." required>
              <Input value={form.username} onChange={set('username')} placeholder="jdoe" autoComplete="username" required minLength={3} />
            </Field>
            <Field label="Password" hint="At least 8 characters. Longer beats complicated." error={error ?? undefined} required>
              <Input type="password" value={form.password} onChange={set('password')} placeholder="••••••••" autoComplete="new-password" required minLength={8} />
            </Field>
            <Button type="submit" className="block" loading={busy}>Create account</Button>
          </form>

          <div className="auth-foot stack gap-2">
            <span>Already registered? <Link to="/login" className="inline-link">Sign in</Link>.</span>
            <span className="muted-more t-xs">
              Creating an account signs you in straight away. We send one verification email; nothing else
              until you ask for it.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
